import * as React from 'react';
// Import Libraries
import axios from 'axios';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import Swal from 'sweetalert2';
import _ from 'lodash';
// Import App Components
import { Link, RouteComponentProps } from 'react-router-dom';
import { UserController } from 'api';
import PanelHeader from '../react-common/PanelHeader';
import PanelBody from '../react-common/PanelBody';
import Tabs from '../react-common/Tabs';
import AnnouncementEdit from './AnnouncementEdit/AnnouncementEdit';
import AnnouncementRecipients from './AnnouncementRecipients/AnnouncementRecipients';

// This specify the available tabs
const tabs = [
  { text: 'Create Announcement', icon: 'pencil-square-o' },
  {
    text: 'Manage Recipients',
    icon: 'users',
    defaultText: 'Manage Recipients',
  },
];

const USER_TYPE_MAP = {
  'Global Admins': 'GLOBAL_ADMIN',
  'Branch Admins': 'BRANCH_ADMIN',
  'Team Admins': 'TEAM_ADMIN',
  Agents: 'AGENT',
};

type userTypes = 'Global Admins' | 'Branch Admins' | 'Team Admins' | 'Agents';

type AnnouncementEditState = {
  timeoutId: number;
  activeTabIndex: number;
  announcement: any;
  recipients: any;
  model?: string;
  isLoading: boolean;
  locations: object[];
  uniqueUsers: object[];
  uniqueUsersPage: number;
  uniqueUsersPerPage: number;
  uniqueCount: number;
};

interface MatchParams {
  announcementId?: string;
}

interface AnnouncementEditViewProps extends RouteComponentProps<MatchParams> {
  userEmail: string;
  authorizedLocations?: Set<string>;
}

class AnnouncementEditView extends React.Component<
  AnnouncementEditViewProps,
  AnnouncementEditState
> {
  constructor(props: AnnouncementEditViewProps) {
    super(props);
    const { userEmail } = props;
    this.state = {
      locations: [],
      timeoutId: null,
      activeTabIndex: 0,
      announcement: {
        replyTo: userEmail,
      },
      recipients: {
        selectedUsers: [],
        selectedGroups: [],
        selectedLocations: [],
        omittedUsers: [],
        userTypeFilters: [],
      },
      uniqueUsers: [],
      uniqueUsersPage: 1,
      uniqueUsersPerPage: 10,
      uniqueCount: 0,
      isLoading: true,
    };
  }

  redirectToListView = () => {
    window.location.replace('#/announcement/list');
  };

  onTabChange =
    (value: string, index: number) =>
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      this.setState({ activeTabIndex: index });
    };

  sendAnnouncement = async () => {
    const { announcement, recipients, uniqueCount } = this.state;
    const { sendLater, delay } = announcement;
    const {
      selectedUsers,
      selectedGroups,
      selectedLocations,
      omittedUsers,
      userTypeFilters,
    } = recipients;
    const postData = {
      ...announcement,
      // TODO: ANNOUNCEMENT-USERIDS
      // Please update backend and chat to recieve an array of string;
      // instead of { id: string }[];
      userIds: selectedUsers.map((user: any) => ({ id: user.id })),
      groups: selectedGroups,
      locations: selectedLocations.reduce(
        (indexMap: any, location: any) => ({
          ...indexMap,
          [location.index]: true,
        }),
        {},
      ),
      omitIds: omittedUsers.map((user: any) => user.id),
      omitRoles: userTypeFilters.map((type: userTypes) => USER_TYPE_MAP[type]),
    };
    const postEndpoint = sendLater && delay ? '/announcement' : '/message';
    // Send/Schedule Announcement
    if (uniqueCount === 0) {
      return Swal.fire({
        type: 'warning',
        title: 'No Recipients',
        text: 'Please select at least one recipient',
      });
    }
    if (sendLater && (!delay || moment(delay) < moment())) {
      return Swal.fire({
        type: 'warning',
        title: 'Time Provided Is Not Valid',
        text: 'Please specify valid time in the future',
      });
    }

    if (
      !postData.subject ||
      !postData.body ||
      postData.subject.trim().length === 0
    ) {
      return Swal.fire({
        type: 'warning',
        title: 'Invalid Announcement',
        text: 'Please double check your announcement to make sure you have added a Subject and some content.',
      });
    }

    const { value } = await Swal.fire({
      type: 'info',
      title: `Are you sure?`,
      text: 'Click "Yes" to send the announcement!',
      showCancelButton: true,
      confirmButtonText: 'Yes',
    });
    if (value) {
      Swal.fire({
        type: 'info',
        title: sendLater
          ? 'Scheduling Announcement...'
          : 'Sending Announcement...',
        showConfirmButton: false,
        html: '<i class="fa fa-spinner fa-spin fa-2x"></i>',
      });
      try {
        await axios.post(postEndpoint, postData);
        return Swal.fire({
          type: 'success',
          title: sendLater ? 'Announcement Scheduled' : 'Announcement Sent',
          onClose: this.redirectToListView,
        });
      } catch (err) {
        return Swal.fire({
          type: 'warning',
          title: 'Invalid Announcement',
          text: 'Please double check your announcement to make sure you have added a Subject and some content.',
        });
      }
    }
  };

  saveAnnouncement = async () => {
    // Save Announcement
    const { announcement } = this.state;
    const { delay, ...cleanedDraft } = announcement;
    if (
      !announcement.subject ||
      !announcement.body ||
      announcement.subject.trim().length === 0
    ) {
      return Swal.fire({
        type: 'warning',
        title: 'Invalid Announcement',
        text: 'Please double check your announcement to make sure you have added a Subject and some content.',
      });
    }
    Swal.fire({
      type: 'info',
      title: 'Saving Draft Announcement...',
      showConfirmButton: false,
      html: '<i class="fa fa-spinner fa-spin fa-2x"></i>',
    });
    try {
      await axios.post('/announcement', cleanedDraft);
      Swal.fire({
        type: 'success',
        title: 'Draft Announcement Saved',
        onClose: this.redirectToListView,
      });
    } catch (err) {
      Swal.fire({
        type: 'warning',
        title: 'Saving Failed',
        text: 'Please make sure that the announcement is valid',
      });
    }
  };

  onAnnouncementChange = (announcement: any) => {
    const { announcement: prevAnnouncement } = this.state;
    const updatedAnnouncement = { ...prevAnnouncement, ...announcement };
    this.setState({ announcement: updatedAnnouncement, isLoading: false });
  };

  onRecipientsChange = (recipients: any) => {
    const clonedRecipients = _.cloneDeep(recipients);
    this.setState(
      { recipients: clonedRecipients },
      this.debouncedUniqueRecipients,
    );
  };

  updateUniqueRecipients = async () => {
    const {
      recipients: {
        selectedUsers,
        selectedGroups,
        selectedLocations,
        omittedUsers,
        userTypeFilters,
      },
      uniqueUsersPage,
      uniqueUsersPerPage,
    } = this.state;
    const userIdsString = selectedUsers.map((user: any) => user.id).join(',');
    const omittedIdsString = omittedUsers.map((user: any) => user.id).join(',');
    const groupIdsString = selectedGroups
      .map((group: any) => group.id)
      .join(',');
    const locationIndexString = selectedLocations
      .map((location: any) => location.index)
      .join(',');
    const userTypeFilterString = userTypeFilters
      .map((type: userTypes) => USER_TYPE_MAP[type])
      .join(',');

    const { users, count } = await UserController.getUniqueUsers({
      page: uniqueUsersPage - 1,
      limit: uniqueUsersPerPage,
      users: userIdsString,
      groups: groupIdsString,
      locations: locationIndexString,
      omits: omittedIdsString,
      typeFilters: userTypeFilterString,
    });

    this.setState({ uniqueUsers: users, uniqueCount: count, isLoading: false });
    // get paginated unique recipients;
  };

  debouncedUniqueRecipients = () => {
    const { timeoutId } = this.state;
    window.clearTimeout(timeoutId);
    const newId = window.setTimeout(this.updateUniqueRecipients, 300);
    this.setState({ timeoutId: newId, isLoading: true });
  };

  getDraftAnnouncement = async (draftAnnouncementId: string) => {
    const { data: announcementDraft } = await axios.get(
      `/announcement/${draftAnnouncementId}`,
    );
    const { locations, createdAt, updatedAt, user, delay, ...cleanedDraft } =
      announcementDraft;
    this.onAnnouncementChange(cleanedDraft);
    if (cleanedDraft.groups || cleanedDraft.userIds) {
      const { recipients } = this.state;
      this.onRecipientsChange({
        ...recipients,
        selectedGroups: cleanedDraft.groups || [],
        selectedUsers: cleanedDraft.userIds.map((id: string) => ({ id })) || [],
      });
    }
  };

  getReuseAnnouncement = async (announcementId: string) => {
    const { data: announcement } = await axios.get(
      `/announcement/${announcementId}`,
    );
    const { subject, body } = announcement;
    this.onAnnouncementChange({ subject, body });
  };

  changeUniqueUsersPage = (uniqueUsersPage: number) => {
    this.setState({ uniqueUsersPage }, this.debouncedUniqueRecipients);
  };

  async componentDidMount() {
    const { match, location } = this.props;
    const searchParams = new URLSearchParams(location.search);
    const draftId = match.params.announcementId;
    const reuseId = searchParams.get('reuse');
    const {
      data: { locations },
    } = await axios.get('/org/locations');

    this.setState({ locations });
    if (draftId) {
      this.getDraftAnnouncement(draftId);
    } else if (reuseId) {
      this.getReuseAnnouncement(reuseId);
    } else {
      this.setState({ isLoading: false });
    }
  }

  render() {
    const {
      activeTabIndex,
      announcement,
      recipients,
      isLoading,
      uniqueUsers,
      uniqueCount,
      uniqueUsersPage,
      uniqueUsersPerPage,
      locations,
    } = this.state;
    const { authorizedLocations } = this.props;
    const { sendLater } = announcement;
    const sendAnnouncementButton = (
      <button
        onClick={this.sendAnnouncement}
        key={uuidv4()}
        className="btn btn-success"
      >
        {sendLater ? 'Schedule' : 'Send'} Announcement
      </button>
    );
    const saveAnnouncementButton = (
      <button
        onClick={this.saveAnnouncement}
        key={uuidv4()}
        className="btn btn-md btn-info"
      >
        Save Draft
      </button>
    );
    const cancelButton = (
      <Link key={uuidv4()} to="/list" className="btn btn-default">
        Cancel
      </Link>
    );
    const extras = [
      sendAnnouncementButton,
      saveAnnouncementButton,
      cancelButton,
    ];
    tabs[1].text = uniqueCount
      ? `${tabs[1].defaultText} (${uniqueCount})`
      : tabs[1].defaultText;
    return (
      <>
        <PanelHeader title="New Announcement" extra={extras} />
        <PanelBody>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignContent: 'flex-start',
              width: '100%',
            }}
          >
            <Tabs
              activeTabIndex={activeTabIndex}
              options={tabs}
              onChange={this.onTabChange}
            />
            <hr style={{ width: '100%' }}></hr>
            {!isLoading && activeTabIndex === 0 && (
              <AnnouncementEdit
                announcement={announcement}
                onChange={this.onAnnouncementChange}
              />
            )}
            {activeTabIndex === 1 && (
              <AnnouncementRecipients
                locations={locations}
                authorizedLocations={authorizedLocations}
                uniqueUsers={uniqueUsers}
                uniqueCount={uniqueCount}
                uniqueUsersPage={uniqueUsersPage}
                uniqueUsersPerPage={uniqueUsersPerPage}
                recipients={recipients}
                isLoading={isLoading}
                changeUniqueUsersPage={this.changeUniqueUsersPage}
                onRecipientsUpdate={this.onRecipientsChange}
              />
            )}
          </div>
        </PanelBody>
      </>
    );
  }
}

export default AnnouncementEditView;
